import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { LibrarySkill, LibrarySource, Skill, SkillService, skillErrorText } from '../../../services/skill.service';

/**
 * The skill library: public skill collections (anthropics/skills, community
 * collections), indexed daily by al-tutor into the LangGraph store and
 * searched here by meaning. One click imports a skill — inactive, with its
 * report, like an upload; the editor opens next.
 */
@Component({
  selector: 'app-skill-library-dialog',
  templateUrl: './skill-library-dialog.component.html',
  styleUrl: './skill-library-dialog.component.scss',
})
export class SkillLibraryDialogComponent implements OnInit, OnDestroy {
  @Input() onImported: (skill: Skill) => void = () => {};

  static readonly PAGE = 30;

  sources: LibrarySource[] = [];
  /** 'all' or a source id; the official collection first */
  source = 'anthropic';
  query = '';
  codeFree = false;

  results: LibrarySkill[] = [];
  loading = false;
  hasMore = false;
  importing: string | null = null;
  error: string | null = null;

  private query$ = new Subject<string>();
  private sub?: Subscription;
  private search?: Subscription;

  constructor(
    protected dialogRef: NbDialogRef<SkillLibraryDialogComponent>,
    private skills: SkillService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.skills.librarySources().subscribe({ next: (s) => (this.sources = s) });
    this.sub = this.query$.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => this.load(true));
    this.load(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.search?.unsubscribe();
  }

  get indexed(): boolean {
    return this.sources.some((s) => !!s.count);
  }

  /** i18n name for known collections ("Anthropic (offiziell)"), else the backend label */
  sourceLabel(id: string): string {
    const key = `pages.skills.library.sources.${id}`;
    const translated = this.t.instant(key);
    return translated !== key ? translated : this.sources.find((s) => s.id === id)?.label ?? id;
  }

  onQuery(value: string): void {
    this.query = value;
    this.query$.next(value);
  }

  onFilter(): void {
    this.load(true);
  }

  load(reset: boolean): void {
    this.search?.unsubscribe();
    this.loading = true;
    this.error = null;
    const offset = reset ? 0 : this.results.length;
    this.search = this.skills
      .searchLibrary({
        q: this.query,
        source: this.source === 'all' ? null : this.source,
        codeFree: this.codeFree,
        limit: SkillLibraryDialogComponent.PAGE,
        offset,
      })
      .subscribe({
        next: (rows) => {
          this.loading = false;
          this.results = reset ? rows : [...this.results, ...rows];
          this.hasMore = rows.length === SkillLibraryDialogComponent.PAGE;
        },
        error: (err) => {
          this.loading = false;
          this.error = skillErrorText(err, this.t);
        },
      });
  }

  importSkill(item: LibrarySkill): void {
    this.importing = item.path;
    this.error = null;
    this.skills.importFromLibrary(item.source, item.path).subscribe({
      next: (skill) => {
        this.importing = null;
        this.onImported(skill);
        this.dialogRef.close();
      },
      error: (err) => {
        this.importing = null;
        this.error = skillErrorText(err, this.t);
      },
    });
  }

  trackItem = (_: number, s: LibrarySkill) => s.source + '/' + s.path;

  close(): void {
    this.dialogRef.close();
  }
}
